import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Loader2, Download, Upload, X, Type, Sparkles, Lock, Palette, Trash2, Zap, Crown, TrendingUp, Users, Target, Rocket } from 'lucide-react';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';
import { TEMPLATES, TEMPLATE_CATEGORIES, getTemplatesByCategory, FREE_TEMPLATE_IDS } from '@/data/templates';
import PaywallPopup from './PaywallPopup';
import UpgradeComparisonTable from './UpgradeComparisonTable';
import OnboardingSlideshow from './OnboardingSlideshow';
import { motion, AnimatePresence } from 'framer-motion';

// LocalStorage key for persisting generated images
const IMAGES_STORAGE_KEY = 'finads_images_data';

// ===== SOCIAL PROOF LIVE TICKER DATA =====
const SOCIAL_PROOF_MESSAGES = [
  { city: "München", product: "PKV", leads: 8, cost: "4,12" },
  { city: "Hamburg", product: "Zahnzusatz", leads: 15, cost: "3,89" },
  { city: "Berlin", product: "BU-Versicherung", leads: 6, cost: "5,23" },
  { city: "Köln", product: "Altersvorsorge", leads: 12, cost: "3,45" },
  { city: "Frankfurt", product: "Rechtsschutz", leads: 9, cost: "4,67" },
  { city: "Stuttgart", product: "Hunde-OP", leads: 22, cost: "2,98" },
  { city: "Düsseldorf", product: "Immobilien-Leads", leads: 4, cost: "8,50" },
  { city: "Dubai", product: "Luxury Real Estate", leads: 2, cost: "12,30" },
];

// ===== LOADING ANIMATION MESSAGES =====
const LOADING_MESSAGES = [
  "Analysiere High-Conversion-Patterns...",
  "Optimiere Kontrast für Meta-Algorithmus...",
  "Wende Premium-Farbpalette an...",
  "Generiere verkaufsstarke Komposition...",
  "Finalisiere Profi-Creative...",
];

// ===== EARNINGS SIMULATOR COMPONENT =====
const EarningsSimulator = ({ isVisible }) => {
  const [values] = useState(() => ({
    agencyValue: Math.floor(Math.random() * 150) + 350, // 350-500€
    timeSaved: Math.floor(Math.random() * 3) + 2 // 2-5 Tage
  }));
  
  if (!isVisible) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="mt-4 p-4 rounded-xl border-2"
      style={{ 
        background: 'linear-gradient(135deg, rgba(0, 242, 255, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)',
        borderColor: 'rgba(0, 242, 255, 0.3)'
      }}
    >
      <div className="flex items-center gap-2 mb-2">
        <TrendingUp className="w-5 h-5 text-cyan-400" />
        <span className="text-cyan-400 font-bold text-sm">EARNINGS SIMULATOR</span>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="text-center p-3 rounded-lg" style={{ backgroundColor: 'rgba(0, 242, 255, 0.1)' }}>
          <div className="text-2xl font-bold text-white">{values.agencyValue} €</div>
          <div className="text-xs text-[rgba(245,245,247,0.5)]">Geschätzter Agentur-Wert</div>
        </div>
        <div className="text-center p-3 rounded-lg" style={{ backgroundColor: 'rgba(255, 92, 0, 0.1)' }}>
          <div className="text-2xl font-bold text-orange-400">{values.timeSaved} Tage</div>
          <div className="text-xs text-[rgba(245,245,247,0.5)]">Zeit gespart</div>
        </div>
      </div>
      <p className="text-xs text-[rgba(245,245,247,0.5)] mt-3 text-center">
        Du hast gerade in 30 Sekunden ein Asset erstellt, für das Agenturen {values.agencyValue}€ berechnen.
      </p>
    </motion.div>
  );
};

// ===== SOCIAL PROOF TICKER COMPONENT =====
const SocialProofTicker = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % SOCIAL_PROOF_MESSAGES.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);
  
  const message = SOCIAL_PROOF_MESSAGES[currentIndex];
  
  return (
    <motion.div
      key={currentIndex}
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="flex items-center gap-2 p-3 rounded-lg mb-4"
      style={{ backgroundColor: 'rgba(0, 242, 255, 0.08)', border: '1px solid rgba(0, 242, 255, 0.2)' }}
    >
      <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
      <Users className="w-4 h-4 text-cyan-400" />
      <span className="text-sm text-[rgba(245,245,247,0.8)]">
        <span className="text-white font-semibold">Gerade eben:</span> Makler aus {message.city} hat{' '}
        <span className="text-cyan-400 font-bold">{message.leads} Leads</span> für {message.product} generiert{' '}
        <span className="text-green-400">(Ø {message.cost}€/Lead)</span>
      </span>
    </motion.div>
  );
};

// ===== ELITE LOCK OVERLAY COMPONENT =====
const EliteLockOverlay = ({ title, metric, onUpgrade }) => (
  <div 
    className="absolute inset-0 flex flex-col items-center justify-center rounded-lg cursor-pointer transition-all hover:bg-black/70"
    style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)', backdropFilter: 'blur(4px)' }}
    onClick={onUpgrade}
  >
    <Lock className="w-8 h-8 text-orange-400 mb-2" />
    <span className="text-white font-bold text-sm text-center px-2">{title}</span>
    {metric && (
      <span className="text-cyan-400 text-xs mt-1 text-center px-2">{metric}</span>
    )}
    <span className="text-orange-400 text-xs mt-2 flex items-center gap-1">
      <Crown className="w-3 h-3" /> Elite freischalten
    </span>
  </div>
);

// ===== SPARTEN-TRIGGER MODAL (Locked Templates) =====
const SpartenTriggerModal = ({ isOpen, onClose, onUpgrade }) => {
  if (!isOpen) return null;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ backgroundColor: 'rgba(11, 14, 20, 0.95)' }}
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 20 }}
          className="max-w-lg w-full rounded-2xl p-6 relative"
          style={{ 
            backgroundColor: '#0B0E14',
            border: '1px solid rgba(255, 92, 0, 0.3)'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Orange Accent Bar */}
          <div className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl" style={{ background: 'linear-gradient(90deg, #FF5C00, #FF8A00, #FF5C00)' }} />
          
          <div className="text-center mb-5">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(255, 92, 0, 0.15)', border: '2px solid #FF5C00' }}>
              <Crown className="w-8 h-8" style={{ color: '#FF5C00' }} />
            </div>
            <h3 className="text-xl font-bold mb-2" style={{ color: '#F5F5F7' }}>
              Schalte alle 22 Profi-Layouts frei
            </h3>
            <p className="text-sm" style={{ color: 'rgba(245, 245, 247, 0.6)' }}>PKV, BU, Immobilien & 19 weitere Sparten</p>
          </div>
          
          <p className="text-sm leading-relaxed mb-5" style={{ color: 'rgba(245, 245, 247, 0.8)' }}>
            Diese Templates basieren auf <span className="font-bold" style={{ color: '#00F2FF' }}>Tausenden erfolgreichen Leads</span>. 
            Die exakten Layouts, die bei Maklern für Conversions unter 5€/Lead sorgen.
          </p>
          
          {/* Pricing Options */}
          <div className="space-y-3 mb-5">
            <div className="p-3 rounded-xl" style={{ backgroundColor: 'rgba(0, 242, 255, 0.05)', border: '1px solid rgba(0, 242, 255, 0.2)' }}>
              <div className="flex justify-between items-center">
                <div>
                  <span className="font-semibold" style={{ color: '#F5F5F7' }}>Starter</span>
                  <span className="text-sm ml-2" style={{ color: 'rgba(245, 245, 247, 0.6)' }}>Alle 22 Templates</span>
                </div>
                <span className="font-bold" style={{ color: '#00F2FF' }}>79 € einmalig</span>
              </div>
            </div>
            <div className="p-3 rounded-xl relative overflow-hidden" style={{ backgroundColor: 'rgba(255, 92, 0, 0.1)', border: '2px solid rgba(255, 92, 0, 0.5)' }}>
              <div className="absolute top-0 right-0 px-2 py-0.5 text-xs font-bold rounded-bl" style={{ backgroundColor: '#FF5C00', color: 'white' }}>EMPFOHLEN</div>
              <div className="flex justify-between items-center">
                <div>
                  <span className="font-semibold" style={{ color: '#F5F5F7' }}>Elite</span>
                  <span className="text-sm ml-2" style={{ color: '#00F2FF' }}>+ Meta-Direkt-Sync</span>
                </div>
                <span className="font-bold" style={{ color: '#FF5C00' }}>799 € einmalig</span>
              </div>
              <p className="text-xs mt-1" style={{ color: 'rgba(245, 245, 247, 0.6)' }}>Nie wieder Facebook Business Manager öffnen</p>
            </div>
          </div>
          
          <Button
            onClick={onUpgrade}
            className="w-full py-5 text-base font-bold rounded-xl"
            style={{ 
              background: 'linear-gradient(135deg, #FF5C00 0%, #FF8A00 100%)',
              color: 'white',
              boxShadow: '0 0 30px rgba(255, 92, 0, 0.4)'
            }}
          >
            <Crown className="w-5 h-5 mr-2" />
            Jetzt freischalten
          </Button>
          
          <p className="text-center text-xs mt-3" style={{ color: 'rgba(245, 245, 247, 0.5)' }}>
            Einmalzahlung. Keine Abo-Falle. Lebenslanger Zugang.
          </p>
          
          <button
            onClick={onClose}
            className="absolute top-3 right-3 transition-colors p-2 rounded-full hover:bg-[rgba(255,255,255,0.05)]/10"
            style={{ color: '#F5F5F7', backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
          >
            <X className="w-5 h-5" />
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// ===== LAUNCH-TRIGGER MODAL (Meta Sync Click) =====
const LaunchTriggerModal = ({ isOpen, onClose, onUpgrade }) => {
  if (!isOpen) return null;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ backgroundColor: 'rgba(11, 14, 20, 0.95)' }}
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 20 }}
          className="max-w-lg w-full rounded-2xl p-6 relative"
          style={{ 
            backgroundColor: '#0B0E14',
            border: '1px solid rgba(0, 242, 255, 0.3)'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Cyan Accent Bar */}
          <div className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl" style={{ backgroundColor: '#00F2FF' }} />
          
          <div className="text-center mb-5">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(0, 242, 255, 0.15)', border: '2px solid #00F2FF' }}>
              <Rocket className="w-8 h-8" style={{ color: '#00F2FF' }} />
            </div>
            <h3 className="text-xl font-bold mb-2" style={{ color: '#F5F5F7' }}>
              45 Minuten im Business Manager – oder 10 Sekunden?
            </h3>
          </div>
          
          {/* Time Comparison */}
          <div className="grid grid-cols-2 gap-3 mb-5">
            <div className="p-3 rounded-xl text-center" style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
              <div className="text-2xl font-bold" style={{ color: 'rgba(245, 245, 247, 0.5)' }}>45+ Min</div>
              <div className="text-xs" style={{ color: 'rgba(245, 245, 247, 0.5)' }}>Manuell im Business Manager</div>
              <div className="text-xs mt-1" style={{ color: 'rgba(245, 245, 247, 0.4)' }}>😫 Kompliziert</div>
            </div>
            <div className="p-3 rounded-xl text-center" style={{ backgroundColor: 'rgba(0, 242, 255, 0.1)', border: '1px solid rgba(0, 242, 255, 0.3)' }}>
              <div className="text-2xl font-bold" style={{ color: '#00F2FF' }}>10 Sek</div>
              <div className="text-xs" style={{ color: 'rgba(245, 245, 247, 0.7)' }}>Mit Elite Direkt-Sync</div>
              <div className="text-xs mt-1" style={{ color: '#00F2FF' }}>🚀 Ein Klick = Live</div>
            </div>
          </div>
          
          <p className="text-sm leading-relaxed mb-5" style={{ color: 'rgba(245, 245, 247, 0.8)' }}>
            Mit dem <span className="font-bold" style={{ color: '#FF5C00' }}>Elite-Paket</span> musst du dich 
            <span className="font-bold" style={{ color: '#00F2FF' }}> nie wieder bei Facebook einloggen</span>, um Ads zu schalten. 
            Dein Creative geht mit einem Klick direkt live.
          </p>
          
          {/* Elite Package Highlight */}
          <div className="p-4 rounded-xl mb-5" style={{ backgroundColor: 'rgba(255, 92, 0, 0.1)', border: '2px solid rgba(255, 92, 0, 0.5)' }}>
            <div className="flex justify-between items-center">
              <div>
                <div className="flex items-center gap-2">
                  <Crown className="w-5 h-5" style={{ color: '#FF5C00' }} />
                  <span className="font-bold" style={{ color: '#F5F5F7' }}>Elite Lifetime</span>
                </div>
                <p className="text-xs mt-1" style={{ color: 'rgba(245, 245, 247, 0.6)' }}>Meta-Sync + Alle 22 Templates + Targeting</p>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold" style={{ color: '#FF5C00' }}>799 €</div>
                <div className="text-xs" style={{ color: 'rgba(245, 245, 247, 0.5)' }}>einmalig</div>
              </div>
            </div>
          </div>
          
          <Button
            onClick={onUpgrade}
            className="w-full py-5 text-base font-bold rounded-xl"
            style={{ 
              background: 'linear-gradient(135deg, #FF5C00 0%, #FF8A00 100%)',
              color: 'white',
              boxShadow: '0 0 30px rgba(255, 92, 0, 0.4)'
            }}
          >
            <Zap className="w-5 h-5 mr-2" />
            Jetzt Elite freischalten & sofort live gehen
          </Button>
          
          <p className="text-center text-xs mt-3" style={{ color: 'rgba(245, 245, 247, 0.5)' }}>
            Nie wieder Agentur. Nie wieder Business Manager. Nur noch Leads.
          </p>
          
          <button
            onClick={onClose}
            className="absolute top-3 right-3 text-[rgba(245,245,247,0.5)] hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// ===== WERT-SIMULATOR POPUP (After Image Generation) =====
const WertSimulatorPopup = ({ isVisible, onUpgrade, onClose }) => {
  if (!isVisible) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 30, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: 30, scale: 0.95 }}
      className="mt-6 p-5 rounded-2xl relative overflow-hidden"
      style={{ 
        backgroundColor: 'rgba(255, 92, 0, 0.08)',
        border: '1px solid rgba(255, 92, 0, 0.3)'
      }}
    >
      <div className="relative z-10">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(255, 92, 0, 0.2)', border: '2px solid #FF5C00' }}>
            <TrendingUp className="w-5 h-5" style={{ color: '#FF5C00' }} />
          </div>
          <div>
            <span className="font-bold text-sm" style={{ color: '#FF5C00' }}>ASSET-CHECK ✓</span>
            <p className="text-sm font-semibold" style={{ color: '#F5F5F7' }}>Professionelles Werbemittel erstellt!</p>
          </div>
        </div>
        
        {/* Agency Comparison */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="p-3 rounded-xl text-center" style={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <div className="text-xl font-bold" style={{ color: 'rgba(245, 245, 247, 0.5)' }}>450 €</div>
            <div className="text-xs" style={{ color: 'rgba(245, 245, 247, 0.5)' }}>Agentur-Preis</div>
            <div className="text-xs mt-1" style={{ color: 'rgba(245, 245, 247, 0.4)' }}>pro Creative</div>
          </div>
          <div className="p-3 rounded-xl text-center" style={{ backgroundColor: 'rgba(0, 242, 255, 0.1)', border: '1px solid rgba(0, 242, 255, 0.3)' }}>
            <div className="text-xl font-bold" style={{ color: '#00F2FF' }}>0 €</div>
            <div className="text-xs" style={{ color: 'rgba(245, 245, 247, 0.7)' }}>Mit FinAds Elite</div>
            <div className="text-xs mt-1" style={{ color: '#00F2FF' }}>unbegrenzt</div>
          </div>
        </div>
        
        {/* Agency Killer Calculator */}
        <div className="p-3 rounded-xl mb-4" style={{ backgroundColor: 'rgba(0, 242, 255, 0.05)', border: '1px solid rgba(0, 242, 255, 0.2)' }}>
          <p className="text-xs mb-2" style={{ color: 'rgba(245, 245, 247, 0.6)' }}>📊 Agency-Killer Rechnung:</p>
          <p className="text-sm leading-relaxed" style={{ color: 'rgba(245, 245, 247, 0.8)' }}>
            Bei <span className="font-bold" style={{ color: '#F5F5F7' }}>2.500 €/Monat</span> Agentur-Kosten amortisiert sich 
            FinAds Elite für dich in <span className="font-bold" style={{ color: '#00F2FF' }}>unter 10 Tagen</span>.
          </p>
        </div>
        
        {/* Two Upgrade Options */}
        <div className="space-y-2 mb-4">
          <div className="p-3 rounded-xl flex justify-between items-center" style={{ backgroundColor: 'rgba(0, 242, 255, 0.05)', border: '1px solid rgba(0, 242, 255, 0.2)' }}>
            <div>
              <span className="font-semibold text-sm" style={{ color: '#F5F5F7' }}>Starter</span>
              <span className="text-xs ml-2" style={{ color: 'rgba(245, 245, 247, 0.6)' }}>22 Templates</span>
            </div>
            <span className="font-bold text-sm" style={{ color: '#00F2FF' }}>79 €</span>
          </div>
          <div className="p-3 rounded-xl flex justify-between items-center relative" style={{ backgroundColor: 'rgba(255, 92, 0, 0.1)', border: '2px solid rgba(255, 92, 0, 0.5)' }}>
            <div className="absolute -top-2 left-3 px-2 py-0.5 text-xs font-bold rounded" style={{ backgroundColor: '#FF5C00', color: 'white' }}>BEST VALUE</div>
            <div>
              <span className="font-semibold text-sm" style={{ color: '#F5F5F7' }}>Elite</span>
              <span className="text-xs ml-2" style={{ color: '#00F2FF' }}>+ Meta-Sync</span>
            </div>
            <span className="font-bold text-sm" style={{ color: '#FF5C00' }}>799 €</span>
          </div>
        </div>
        
        <Button
          onClick={onUpgrade}
          className="w-full py-4 text-sm font-bold rounded-xl"
          style={{ 
            background: 'linear-gradient(135deg, #FF5C00 0%, #FF8A00 100%)',
            color: 'white',
            boxShadow: '0 0 20px rgba(255, 92, 0, 0.3)'
          }}
        >
          <Crown className="w-4 h-4 mr-2" />
          Jetzt upgraden – Nie wieder Agentur-Gebühren
        </Button>
      </div>
      
      <button
        onClick={onClose}
        className="absolute top-3 right-3 transition-colors z-20"
        style={{ color: 'rgba(245, 245, 247, 0.5)' }}
      >
        <X className="w-4 h-4" />
      </button>
    </motion.div>
  );
};

// ===== TARGETING-TRIGGER MODAL (Locked Targeting Options) =====
const TargetingTriggerModal = ({ isOpen, onClose, onUpgrade }) => {
  if (!isOpen) return null;
  
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ backgroundColor: 'rgba(11, 14, 20, 0.95)' }}
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 20 }}
          className="max-w-lg w-full rounded-2xl p-6 relative"
          style={{ 
            backgroundColor: '#0B0E14',
            border: '1px solid rgba(167, 139, 250, 0.3)'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Purple Accent Bar */}
          <div className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl" style={{ backgroundColor: '#A78BFA' }} />
          
          <div className="text-center mb-5">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ backgroundColor: 'rgba(167, 139, 250, 0.15)', border: '2px solid #A78BFA' }}>
              <Target className="w-8 h-8" style={{ color: '#A78BFA' }} />
            </div>
            <h3 className="text-xl font-bold mb-2" style={{ color: '#F5F5F7' }}>
              Hör auf zu raten. Nutze, was funktioniert.
            </h3>
          </div>
          
          {/* Blurred Preview - Targeting Teaser */}
          <div className="relative rounded-xl overflow-hidden mb-5" style={{ backgroundColor: 'rgba(167, 139, 250, 0.05)', border: '1px solid rgba(167, 139, 250, 0.2)' }}>
            <div className="p-4" style={{ filter: 'blur(4px)', userSelect: 'none' }}>
              <div className="text-sm mb-2" style={{ color: 'rgba(245, 245, 247, 0.7)' }}>🎯 Top-Performer Targeting:</div>
              <div className="space-y-2 text-xs" style={{ color: 'rgba(245, 245, 247, 0.5)' }}>
                <div>• Alter: 45-65 • Einkommen: Top 10%</div>
                <div>• Interessen: Finanzen, Immobilien, Vermögen...</div>
                <div>• Verhalten: Wohlhabende Käufer, Investoren...</div>
                <div>• Lookalike: PKV-Wechsler 2% Ähnlichkeit...</div>
              </div>
            </div>
            <div className="absolute inset-0 flex items-center justify-center" style={{ backgroundColor: 'rgba(11, 14, 20, 0.7)' }}>
              <div className="text-center">
                <Lock className="w-8 h-8 mx-auto mb-2" style={{ color: '#A78BFA' }} />
                <span className="font-bold text-sm" style={{ color: '#F5F5F7' }}>Du siehst 10% des Targetings</span>
              </div>
            </div>
          </div>
          
          <p className="text-sm leading-relaxed mb-5" style={{ color: 'rgba(245, 245, 247, 0.8)' }}>
            Das volle Experten-Setup inkl. der <span className="font-bold" style={{ color: '#A78BFA' }}>Investoren-Blueprints</span> kostet 
            dich weniger als ein Abendessen. Erreiche gezielt vermögende Investoren oder wechselwillige PKV-Kandidaten.
          </p>
          
          {/* Targeting Add-on Price */}
          <div className="p-4 rounded-xl mb-5" style={{ backgroundColor: 'rgba(167, 139, 250, 0.1)', border: '2px solid rgba(167, 139, 250, 0.5)' }}>
            <div className="flex justify-between items-center">
              <div>
                <div className="flex items-center gap-2">
                  <Target className="w-5 h-5" style={{ color: '#A78BFA' }} />
                  <span className="font-bold" style={{ color: '#F5F5F7' }}>Targeting-Helfer Add-on</span>
                </div>
                <p className="text-xs mt-1" style={{ color: 'rgba(245, 245, 247, 0.6)' }}>Alle Profi-Zielgruppen + Investoren-Listen</p>
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold" style={{ color: '#A78BFA' }}>49 €</div>
                <div className="text-xs" style={{ color: 'rgba(245, 245, 247, 0.5)' }}>einmalig</div>
              </div>
            </div>
          </div>
          
          <Button
            onClick={onUpgrade}
            className="w-full py-5 text-base font-bold rounded-xl"
            style={{ 
              backgroundColor: '#A78BFA',
              color: '#0B0E14'
            }}
          >
            <Target className="w-5 h-5 mr-2" />
            Für 49 € Experten-Targeting freischalten
          </Button>
          
          <p className="text-center text-xs mt-3" style={{ color: 'rgba(245, 245, 247, 0.5)' }}>
            Spare tausende Euro an Testbudget. Zielgruppen der Top-Performer.
          </p>
          
          <button
            onClick={onClose}
            className="absolute top-3 right-3 transition-colors p-2 rounded-full hover:bg-[rgba(255,255,255,0.05)]/10"
            style={{ color: '#F5F5F7', backgroundColor: 'rgba(255, 255, 255, 0.1)' }}
          >
            <X className="w-5 h-5" />
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// ===== MAGIC LOADING COMPONENT =====
const MagicLoadingOverlay = ({ isLoading }) => {
  const [messageIndex, setMessageIndex] = useState(0);
  
  useEffect(() => {
    if (!isLoading) return;
    const interval = setInterval(() => {
      setMessageIndex((prev) => (prev + 1) % LOADING_MESSAGES.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [isLoading]);
  
  if (!isLoading) return null;
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(11, 14, 20, 0.95)' }}
    >
      <div className="text-center max-w-md">
        {/* Animated Logo/Scan Effect */}
        <div className="relative w-48 h-48 mx-auto mb-8">
          <div 
            className="absolute inset-0 rounded-2xl"
            style={{ 
              background: 'linear-gradient(135deg, rgba(255, 92, 0, 0.2) 0%, rgba(0, 242, 255, 0.2) 100%)',
              border: '2px solid rgba(0, 242, 255, 0.3)'
            }}
          />
          {/* Scanning line animation */}
          <motion.div
            className="absolute left-0 right-0 h-1 rounded-full"
            style={{ backgroundColor: '#00F2FF', boxShadow: '0 0 20px #00F2FF' }}
            animate={{ top: ['0%', '100%', '0%'] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'linear' }}
          />
          <div className="absolute inset-0 flex items-center justify-center">
            <Sparkles className="w-16 h-16 text-orange-400 animate-pulse" />
          </div>
        </div>
        
        {/* Loading Message */}
        <AnimatePresence mode="wait">
          <motion.p
            key={messageIndex}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="text-lg font-semibold mb-4"
            style={{ color: '#00F2FF' }}
          >
            {LOADING_MESSAGES[messageIndex]}
          </motion.p>
        </AnimatePresence>
        
        {/* Progress Bar */}
        <div className="w-64 h-2 mx-auto rounded-full overflow-hidden" style={{ backgroundColor: 'rgba(255, 255, 255, 0.1)' }}>
          <motion.div
            className="h-full rounded-full"
            style={{ background: 'linear-gradient(90deg, #FF5C00, #00F2FF)' }}
            animate={{ width: ['0%', '100%'] }}
            transition={{ duration: 10, ease: 'linear' }}
          />
        </div>
        
        <p className="text-[rgba(245,245,247,0.5)] text-sm mt-4">
          Dein Profi-Creative wird generiert...
        </p>
      </div>
    </motion.div>
  );
};

const ImageGenerator = () => {
  const [prompt, setPrompt] = useState('');
  const [loading, setLoading] = useState(false);
  const [images, setImages] = useState([]);
  const [canDownload, setCanDownload] = useState(true);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);
  const [style, setStyle] = useState('realistic');
  const [format, setFormat] = useState('instagram_feed');
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoPosition, setLogoPosition] = useState('bottom-right');
  const [logoSize, setLogoSize] = useState([15]);
  
  // ===== NEW: Conversion Optimization States =====
  const [showEarningsSimulator, setShowEarningsSimulator] = useState(false);
  const [showMagicLoading, setShowMagicLoading] = useState(false);
  const [showWertSimulator, setShowWertSimulator] = useState(false);
  const [showUpgradeTable, setShowUpgradeTable] = useState(false);
  const [showOnboardingSlideshow, setShowOnboardingSlideshow] = useState(false);
  const [hasShownOnboarding, setHasShownOnboarding] = useState(() => {
    // Check if user has already seen the onboarding
    return localStorage.getItem('finads_onboarding_shown') === 'true';
  });
  
  // ===== Trigger Modals States =====
  const [showSpartenTrigger, setShowSpartenTrigger] = useState(false);
  const [showLaunchTrigger, setShowLaunchTrigger] = useState(false);
  const [showTargetingTrigger, setShowTargetingTrigger] = useState(false);
  
  // Unsplash Stock Images State
  const [imageSource, setImageSource] = useState('ai'); // 'ai' or 'stock'
  const [stockSearchQuery, setStockSearchQuery] = useState('');
  const [stockImages, setStockImages] = useState([]);
  const [selectedStockImage, setSelectedStockImage] = useState(null);
  const [loadingStock, setLoadingStock] = useState(false);
  
  // Optionaler Text für KI-Integration (wird direkt von Nano Banana ins Bild integriert)
  const [adText, setAdText] = useState('');
  
  // Product Template selection
  const [selectedProductTemplate, setSelectedProductTemplate] = useState('');
  
  // Text overlay states (für nachträgliche Bearbeitung)
  const [selectedImageForText, setSelectedImageForText] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);
  const [textOverlay, setTextOverlay] = useState('');
  const [textFontSize, setTextFontSize] = useState([60]);
  const [textPosition, setTextPosition] = useState('bottom');
  const [textColor, setTextColor] = useState('#FFFFFF');
  const [textBackground, setTextBackground] = useState('');
  const [textFont, setTextFont] = useState('DejaVuSans');
  const [addingText, setAddingText] = useState(false);

  // Ad Creative states
  const [selectedImageForAd, setSelectedImageForAd] = useState(null);
  const [selectedAdIndex, setSelectedAdIndex] = useState(null);
  const [adHeadline, setAdHeadline] = useState('');
  const [adSubtext, setAdSubtext] = useState('');
  const [adCta, setAdCta] = useState('Mehr erfahren');
  const [adTemplate, setAdTemplate] = useState('modern');

  // Load persisted images on mount
  useEffect(() => {
    try {
      const savedData = localStorage.getItem(IMAGES_STORAGE_KEY);
      if (savedData) {
        const parsed = JSON.parse(savedData);
        if (parsed.images && parsed.images.length > 0) {
          setImages(parsed.images);
        }
        if (parsed.prompt) setPrompt(parsed.prompt);
        if (parsed.style) setStyle(parsed.style);
        if (parsed.format) setFormat(parsed.format);
        if (parsed.selectedProductTemplate) setSelectedProductTemplate(parsed.selectedProductTemplate);
      }
    } catch (e) {
      console.error('Error loading saved images data:', e);
    }
  }, []);

  // Save to localStorage whenever images change
  useEffect(() => {
    if (images.length > 0) {
      try {
        localStorage.setItem(IMAGES_STORAGE_KEY, JSON.stringify({
          images,
          prompt,
          style,
          format,
          selectedProductTemplate,
          savedAt: new Date().toISOString()
        }));
      } catch (e) {
        console.error('Error saving images data:', e);
      }
    }
  }, [images, prompt, style, format, selectedProductTemplate]);

  // Clear saved images
  const clearSavedImages = () => {
    localStorage.removeItem(IMAGES_STORAGE_KEY);
    setImages([]);
    setPrompt('');
    setSelectedProductTemplate('');
    toast.success('Gespeicherte Bilder gelöscht');
  };
  const [adBrandColor, setAdBrandColor] = useState('#667eea');
  const [creatingAd, setCreatingAd] = useState(false);

  // Load subscription status on mount
  useEffect(() => {
    const checkSubscription = async () => {
      try {
        const response = await axiosInstance.get('/subscription/status');
        const status = response.data.subscription_status;
        setHasSubscription(status === 'active' || status === 'lifetime');
      } catch (error) {
        setHasSubscription(false);
      }
    };
    checkSubscription();
  }, []);

  // Check if template is available for current user
  const isTemplateAvailable = (template) => {
    if (hasSubscription) return true;
    return template.isFree === true || FREE_TEMPLATE_IDS.includes(template.id);
  };

  const imageFormats = [
    { value: 'instagram_feed', label: 'Instagram Feed (Quadratisch)', size: '1080x1080' },
    { value: 'instagram_story', label: 'Instagram Story (Vertikal)', size: '1080x1920' },
    { value: 'instagram_reel', label: 'Instagram Reel (Vertikal)', size: '1080x1920' },
    { value: 'facebook_feed', label: 'Facebook Feed', size: '1200x630' },
    { value: 'facebook_story', label: 'Facebook Story (Vertikal)', size: '1080x1920' },
    { value: 'facebook_ad', label: 'Facebook Ad', size: '1200x628' },
    { value: 'linkedin_post', label: 'LinkedIn Post', size: '1200x627' },
    { value: 'twitter_post', label: 'Twitter/X Post', size: '1200x675' }
  ];

  const imageStyles = [
    { value: 'realistic', label: 'Realistisch / Fotografie', description: 'Professionelle, fotorealistische Bilder' },
    { value: 'comic', label: 'Comic / Cartoon', description: 'Comic-Style mit kräftigen Farben' },
    { value: 'retro', label: 'Retro / Vintage', description: '70er Jahre Ästhetik, nostalgisch' },
    { value: 'modern', label: 'Modern / Minimalistisch', description: 'Klares, zeitgenössisches Design' },
    { value: 'artistic', label: 'Künstlerisch / Gemälde', description: 'Wie ein Ölgemälde oder Kunstwerk' },
    { value: '3d_render', label: '3D Render', description: 'Moderne 3D-Grafik, CGI-Style' },
    { value: 'black_white', label: 'Schwarz-Weiß', description: 'Monochromes, klassisches Foto' },
    { value: 'illustration', label: 'Illustration', description: 'Digitale Illustration, handgezeichnet' }
  ];

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Datei zu groß. Maximal 5MB erlaubt.');
        return;
      }
      if (!file.type.startsWith('image/')) {
        toast.error('Bitte nur Bilddateien hochladen.');
        return;
      }
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
      toast.success('Logo hochgeladen!');
    }
  };

  const removeLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    toast.info('Logo entfernt');
  };

  // Stockbild-Funktionen
  const searchStockImages = async (query = stockSearchQuery) => {
    if (!query.trim()) {
      toast.error('Bitte gib einen Suchbegriff ein');
      return;
    }
    
    setLoadingStock(true);
    try {
      const response = await axiosInstance.get(`/unsplash/search`, {
        params: { query, per_page: 15 }
      });
      setStockImages(response.data.results);
      toast.success(`${response.data.total} Bilder gefunden!`);
    } catch (error) {
      toast.error('Fehler beim Laden der Stockbilder');
      console.error(error);
    } finally {
      setLoadingStock(false);
    }
  };

  const selectStockImage = (image) => {
    setSelectedStockImage(image);
    toast.success('Stockbild ausgewählt!');
  };


  const applyTemplate = (templateId) => {
    const template = TEMPLATES.find(t => t.id === templateId);
    if (!template) return;

    // Apply template settings
    setPrompt(template.prompt);
    setStyle(template.style);
    setFormat(template.format);
    
    // Nur den kurzen imageText verwenden - NICHT die bulletPoints kombinieren!
    // Nano Banana funktioniert am besten mit kurzen, knackigen Texten (2-4 Wörter)
    setAdText(template.imageText || '');
    
    // Apply logo position
    setLogoPosition(template.logoPosition);
    
    toast.success(`Template "${template.name}" angewendet!`);
  };

  const handleGenerate = async () => {
    // STOCKBILDER: Wenn Stock ausgewählt, nur das ausgewählte Bild verwenden
    if (imageSource === 'stock') {
      if (!selectedStockImage) {
        toast.error('Bitte wähle zuerst ein Stockbild aus der Galerie');
        return;
      }
      
      setLoading(true);
      try {
        // Zeige das Stockbild direkt an (verwende thumb URL für Vorschau)
        setImages([selectedStockImage.url]); // Verwende die regular URL statt base64
        setCanDownload(true);
        toast.success('Stockbild bereit!');
      } catch (error) {
        toast.error('Fehler beim Laden des Stockbildes');
      } finally {
        setLoading(false);
        setShowMagicLoading(false);
      }
      return;
    }
    
    // KI-GENERIERUNG
    if (!prompt.trim()) {
      toast.error('Bitte geben Sie eine Beschreibung ein');
      return;
    }

    setLoading(true);
    setShowMagicLoading(true); // Show Magic Loading Animation
    setShowEarningsSimulator(false); // Reset earnings simulator
    
    try {
      const formData = new FormData();
      formData.append('prompt', prompt);
      formData.append('count', '4');
      formData.append('style', style);
      formData.append('format', format);
      
      // Optionaler Text - wird direkt in den Nano Banana Prompt integriert
      if (adText && adText.trim()) {
        formData.append('ad_text', adText.trim());
      }
      
      // Insurance Category für professionelle Prompt-Templates
      if (selectedProductTemplate && selectedProductTemplate.trim()) {
        formData.append('insurance_category', selectedProductTemplate.trim());
      }
      
      if (logoFile) {
        formData.append('logo', logoFile);
        formData.append('logo_position', logoPosition);
        formData.append('logo_size', logoSize[0].toString());
      }

      const response = await axiosInstance.post('/generate/images', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      setImages(response.data.images);
      setCanDownload(response.data.can_download); // Set download permission
      setShowEarningsSimulator(true); // Show earnings simulator after generation
      
      // Show Wert-Simulator for free users after successful generation
      if (!hasSubscription) {
        setShowWertSimulator(true);
        
        // Show Onboarding Slideshow for first-time free users
        if (!hasShownOnboarding) {
          // 5-6 second delay to let the user explore their creative first
          setTimeout(() => {
            setShowOnboardingSlideshow(true);
            setHasShownOnboarding(true);
            localStorage.setItem('finads_onboarding_shown', 'true');
          }, 5500);
        }
      }
      
      toast.success('Bilder erfolgreich generiert!');
      
      // Notify other components that new content was created
      window.dispatchEvent(new CustomEvent('contentCreated', { detail: { type: 'image' } }));
    } catch (error) {
      // Check if it's a paywall error (403) - only show popup, no toast
      if (error.response?.status === 403) {
        setShowPaywall(true);
        return; // Exit early, don't show any error message
      }
      
      // Only show toast for non-paywall errors
      toast.error(error.response?.data?.detail || 'Fehler beim Generieren');
    } finally {
      setLoading(false);
      setShowMagicLoading(false);
    }
  };

  const handleDownload = (imageData, index) => {
    if (!canDownload) {
      setShowPaywall(true);
      return;
    }
    
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${imageData}`;
    link.download = `finads-image-${index + 1}.png`;
    link.click();
    toast.success('Bild heruntergeladen!');
  };

  const openTextDialog = (image, index) => {
    setSelectedImageForText(image);
    setSelectedImageIndex(index);
    setTextOverlay('');
    setTextFontSize([60]);
    setTextPosition('bottom');
    setTextColor('#FFFFFF');
    setTextBackground('');
  };

  const handleAddText = async () => {
    if (!textOverlay.trim()) {
      toast.error('Bitte Text eingeben');
      return;
    }

    setAddingText(true);
    try {
      const response = await axiosInstance.post('/add-text-to-image', {
        image_data: selectedImageForText,
        text: textOverlay,
        font_size: textFontSize[0],
        font_family: textFont,
        position: textPosition,
        text_color: textColor,
        background_color: textBackground || null
      }, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      // Update the image in the images array
      const newImages = [...images];
      newImages[selectedImageIndex] = response.data.image;
      setImages(newImages);
      
      toast.success('Text erfolgreich hinzugefügt!');
      setSelectedImageForText(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Hinzufügen von Text');
    } finally {
      setAddingText(false);
    }
  };

  const openAdCreativeDialog = (image, index) => {
    setSelectedImageForAd(image);
    setSelectedAdIndex(index);
    setAdHeadline('');
    setAdSubtext('');
    setAdCta('Mehr erfahren');
    setAdTemplate('modern');
    setAdBrandColor('#667eea');
  };

  const handleCreateAdCreative = async () => {
    if (!adHeadline.trim()) {
      toast.error('Bitte Überschrift eingeben');
      return;
    }

    setCreatingAd(true);
    try {
      const formData = new FormData();
      formData.append('image_data', selectedImageForAd);
      formData.append('design_template', adTemplate);
      formData.append('headline', adHeadline);
      formData.append('subtext', adSubtext);
      formData.append('cta_text', adCta);
      formData.append('brand_color', adBrandColor);

      const response = await axiosInstance.post('/create-ad-creative', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      // Update the image in the images array
      const newImages = [...images];
      newImages[selectedAdIndex] = response.data.image;
      setImages(newImages);
      
      toast.success('Ad-Creative erfolgreich erstellt!');
      setSelectedImageForAd(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen des Ad-Creatives');
    } finally {
      setCreatingAd(false);
    }
  };

  // Canva integration removed - direct download only

  const adTemplates = [
    { value: 'modern', label: 'Modern', description: 'Gradient-Overlay unten mit Text' },
    { value: 'bold', label: 'Bold', description: 'Große farbige Box unten' },
    { value: 'minimal', label: 'Minimal', description: 'Einfache weiße Box mit Text' },
    { value: 'top_banner', label: 'Top Banner', description: 'Farbiger Banner oben' }
  ];

  const selectedStyle = imageStyles.find(s => s.value === style);
  const selectedFormat = imageFormats.find(f => f.value === format);
  const selectedTemplate = adTemplates.find(t => t.value === adTemplate);

  return (
    <>
      {/* Magic Loading Overlay */}
      <AnimatePresence>
        {showMagicLoading && <MagicLoadingOverlay isLoading={showMagicLoading} />}
      </AnimatePresence>
      
      <div className="space-y-6" data-testid="image-generator">
        {/* Social Proof Ticker - Always visible for free users */}
        {!hasSubscription && <SocialProofTicker />}
        
        {/* Meta Sync Button - Launch Trigger for Free Users */}
        {!hasSubscription && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="relative cursor-pointer"
            onClick={() => setShowLaunchTrigger(true)}
          >
            <Button
              className="w-full py-6 text-lg font-bold relative overflow-hidden transition-all hover:scale-[1.02]"
              style={{ 
                background: 'linear-gradient(135deg, rgba(0, 242, 255, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)',
                border: '2px solid rgba(0, 242, 255, 0.4)',
                color: '#00F2FF'
              }}
            >
              <Lock className="w-5 h-5 mr-2" />
              <span>JETZT LIVE SCHALTEN – NUR META-LOGIN</span>
              <Rocket className="w-5 h-5 ml-2" />
            </Button>
            <div className="absolute -top-2 -right-2">
              <span className="px-2 py-1 text-xs font-bold rounded-full animate-pulse" style={{ backgroundColor: '#FF5C00', color: 'white' }}>
                ELITE
              </span>
            </div>
            <p className="text-xs text-center mt-2" style={{ color: 'rgba(0, 242, 255, 0.7)' }}>
              <span className="font-semibold">Agency-Killer:</span> Mit einem Klick direkt auf Facebook & Instagram live – ohne den komplizierten Werbeanzeigenmanager.
            </p>
          </motion.div>
        )}
        
        {/* Template Selection Card */}
        <Card className="shadow-lg border-2" style={{ borderColor: 'rgba(139, 92, 246, 0.3)', background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.05) 0%, rgba(236, 72, 153, 0.05) 100%)' }}>
          <CardHeader>
            <CardTitle className="text-2xl flex items-center gap-2" style={{ color: '#F5F5F7' }}>
              <Sparkles className="w-6 h-6 text-[#A78BFA]" />
              Vorlagen für Versicherungen & Finanzen
              {!hasSubscription && (
                <span className="ml-auto text-xs px-2 py-1 rounded-full" style={{ backgroundColor: 'rgba(255, 92, 0, 0.2)', color: '#FF5C00' }}>
                  3/22 freigeschaltet
                </span>
              )}
            </CardTitle>
            <p className="text-sm text-[rgba(245,245,247,0.5)] mt-2">
              Wählen Sie eine vorgefertigte Vorlage und passen Sie diese nach Belieben an
            </p>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="template-select" className="text-[rgba(245,245,247,0.8)]">Vorlage auswählen</Label>
                <Select 
                  value={selectedProductTemplate} 
                  onValueChange={(value) => {
                    const template = TEMPLATES.find(t => t.id === value);
                    if (template && !isTemplateAvailable(template)) {
                      // Show Sparten-Trigger for locked templates
                      setShowSpartenTrigger(true);
                      return;
                    }
                    setSelectedProductTemplate(value);
                  }}
                >
                  <SelectTrigger id="template-select" className="h-auto py-3 bg-slate-800/50 border-slate-700 text-white">
                    <SelectValue placeholder="Bitte wählen Sie eine Vorlage..." />
                  </SelectTrigger>
                  <SelectContent className="max-h-[400px] bg-slate-800 border-slate-700">
                    {TEMPLATE_CATEGORIES.map((category) => (
                      <div key={category}>
                        <div className="px-2 py-1.5 text-xs font-semibold text-[rgba(245,245,247,0.5)] bg-slate-900/50">
                          {category}
                        </div>
                        {getTemplatesByCategory(category).map((template) => {
                          const available = isTemplateAvailable(template);
                          return (
                            <SelectItem 
                              key={template.id} 
                              value={template.id}
                              className={`${!available ? 'opacity-60' : ''} text-white hover:bg-slate-700`}
                            >
                              <div className="flex items-center gap-2 w-full">
                                <span className="text-lg">{template.bulletEmoji}</span>
                                <span className={!available ? 'text-[rgba(245,245,247,0.5)]' : 'text-white'}>{template.name}</span>
                                {!available && (
                                  <div className="ml-auto flex items-center gap-1">
                                    <Lock className="w-3 h-3 text-orange-400" />
                                    <Crown className="w-3 h-3 text-orange-400" />
                                  </div>
                                )}
                                {template.isFree && (
                                  <span className="ml-auto text-xs px-1.5 py-0.5 rounded" style={{ backgroundColor: 'rgba(0, 242, 255, 0.2)', color: '#00F2FF' }}>
                                    Gratis
                                  </span>
                                )}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </div>
                    ))}
                  </SelectContent>
                </Select>
                {!hasSubscription && (
                  <p className="text-xs text-[rgba(245,245,247,0.5)] mt-1">
                    3 kostenlose Templates verfügbar. <button onClick={() => setShowSpartenTrigger(true)} className="font-semibold hover:underline" style={{ color: '#C9A227' }}>Upgrade für alle 22 Templates</button>
                  </p>
              )}
            </div>
            <div className="flex items-end">
              <Button
                onClick={() => selectedProductTemplate && applyTemplate(selectedProductTemplate)}
                disabled={!selectedProductTemplate}
                className="w-full h-12"
                style={{
                  background: selectedProductTemplate ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : undefined
                }}
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Vorlage anwenden
              </Button>
            </div>
          </div>
          {selectedProductTemplate && (
            <div className="mt-4 p-4 bg-[rgba(255,255,255,0.03)] rounded-lg border-2 border-[rgba(0,242,255,0.3)]">
              <p className="text-sm text-[rgba(245,245,247,0.8)]">
                <strong>Vorschau:</strong> {TEMPLATES.find(t => t.id === selectedProductTemplate)?.name}
              </p>
              <p className="text-xs text-[rgba(245,245,247,0.5)] mt-1">
                Füllt automatisch: Prompt, Stil, Text und Format
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="shadow-lg border-2 border-[rgba(0,242,255,0.2)] relative">
        <CardHeader>
          <CardTitle className="text-2xl">Bild Generator</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="image-prompt">Bildbeschreibung</Label>
            <Textarea
              id="image-prompt"
              placeholder="z.B. Professioneller Finanzberater im modernen Büro, Familie plant Zukunft, Handschlag bei Vertragsunterzeichnung..."
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={4}
              data-testid="image-prompt-input"
              className="resize-none"
            />
            <div className="bg-[rgba(0,242,255,0.1)] p-3 rounded-lg border border-[rgba(0,242,255,0.3)]">
              <p className="text-sm text-[#00F2FF]">
                <strong>💡 Tipp:</strong> Die Bilder werden ohne Text generiert. Nutzen Sie dann <strong>&ldquo;Ad-Creative erstellen&rdquo;</strong> um professionelle Texte hinzuzufügen!
              </p>
            </div>
          </div>

          {/* Style Selection */}
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Label htmlFor="image-style" className="flex items-center gap-2">
                <Palette className="w-4 h-4" />
                Bildstil
              </Label>
              <Select value={style} onValueChange={setStyle}>
                <SelectTrigger data-testid="image-style-select" className="h-auto py-3">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {imageStyles.map((styleOption) => (
                    <SelectItem 
                      key={styleOption.value} 
                      value={styleOption.value}
                      data-testid={`style-${styleOption.value}`}
                    >
                      <div className="py-1">
                        <div className="font-semibold">{styleOption.label}</div>
                        <div className="text-xs text-[rgba(245,245,247,0.6)]">{styleOption.description}</div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedStyle && (
                <p className="text-sm text-[rgba(245,245,247,0.6)] bg-[rgba(0,242,255,0.05)] p-2 rounded">
                  ✨ {selectedStyle.description}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="image-format" className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <rect x="3" y="3" width="18" height="18" rx="2" strokeWidth="2"/>
                  <line x1="9" y1="3" x2="9" y2="21" strokeWidth="2"/>
                  <line x1="15" y1="3" x2="15" y2="21" strokeWidth="2"/>
                </svg>
                Social Media Format
              </Label>
              <Select value={format} onValueChange={setFormat}>
                <SelectTrigger data-testid="image-format-select" className="h-auto py-3">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {imageFormats.map((formatOption) => (
                    <SelectItem 
                      key={formatOption.value} 
                      value={formatOption.value}
                      data-testid={`format-${formatOption.value}`}
                    >
                      <div className="py-1">
                        <div className="font-semibold">{formatOption.label}</div>
                        <div className="text-xs text-[rgba(245,245,247,0.6)]">{formatOption.size}</div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedFormat && (
                <p className="text-sm text-[rgba(245,245,247,0.6)] bg-[rgba(0,242,255,0.1)] p-2 rounded">
                  📐 {selectedFormat.size} Pixel
                </p>
              )}
            </div>
          </div>

          {/* Optionaler Text für das Bild (KI-integriert) */}
          <div className="border-2 border-dashed border-emerald-500/30 rounded-lg p-6 bg-emerald-500/5">
            <div className="flex items-start gap-4">
              <Type className="w-6 h-6 text-emerald-400 mt-1" />
              <div className="flex-1 space-y-4">
                <div>
                  <h3 className="font-semibold text-[#F5F5F7] mb-2">Text im Bild (Optional)</h3>
                  <p className="text-sm text-[rgba(245,245,247,0.6)] mb-3">
                    Geben Sie optional einen Text ein, den die KI direkt ins Bild integriert. 
                    Nano Banana erstellt professionelle Typografie automatisch.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="ad-text">Ihr Werbetext</Label>
                  <Input
                    id="ad-text"
                    placeholder="z.B. Jetzt beraten lassen! | Kostenlose Erstberatung | Bis zu 50% sparen"
                    value={adText}
                    onChange={(e) => setAdText(e.target.value)}
                    data-testid="ad-text-input"
                    className="text-lg"
                  />
                  <p className="text-xs text-[rgba(245,245,247,0.5)]">
                    Trennen Sie mehrere Punkte mit | für bessere Struktur
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Logo Upload Section */}
          <div className="border-2 border-dashed border-[rgba(0,242,255,0.3)] rounded-lg p-6 bg-[rgba(0,242,255,0.05)]/50">
            <div className="flex items-start gap-4">
              <Upload className="w-6 h-6 text-[#00F2FF] mt-1" />
              <div className="flex-1 space-y-4">
                <div>
                  <h3 className="font-semibold text-[#F5F5F7] mb-2">Logo / Eigenes Bild hinzufügen (Optional)</h3>
                  <p className="text-sm text-[rgba(245,245,247,0.6)] mb-3">
                    Laden Sie Ihr Logo oder ein eigenes Bild hoch, um es auf den generierten Bildern zu platzieren.
                  </p>
                </div>

                {!logoPreview ? (
                  <div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                      id="logo-upload"
                      data-testid="logo-upload-input"
                    />
                    <label htmlFor="logo-upload">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => document.getElementById('logo-upload').click()}
                        data-testid="logo-upload-button"
                        className="cursor-pointer"
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Bild hochladen
                      </Button>
                    </label>
                    <p className="text-xs text-[rgba(245,245,247,0.5)] mt-2">PNG, JPG, JPEG bis 5MB</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <img
                        src={logoPreview}
                        alt="Logo Preview"
                        className="w-24 h-24 object-contain border-2 border-slate-600 rounded-lg bg-slate-800/50 p-2"
                        data-testid="logo-preview"
                      />
                      <Button
                        onClick={removeLogo}
                        variant="outline"
                        size="sm"
                        data-testid="remove-logo-button"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Entfernen
                      </Button>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="logo-position">Position</Label>
                        <Select value={logoPosition} onValueChange={setLogoPosition}>
                          <SelectTrigger data-testid="logo-position-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="top-left">Oben Links</SelectItem>
                            <SelectItem value="top-right">Oben Rechts</SelectItem>
                            <SelectItem value="bottom-left">Unten Links</SelectItem>
                            <SelectItem value="bottom-right">Unten Rechts</SelectItem>
                            <SelectItem value="center">Zentriert</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="logo-size">
                          Größe: {logoSize[0]}%
                        </Label>
                        <Slider
                          id="logo-size"
                          value={logoSize}
                          onValueChange={setLogoSize}
                          min={5}
                          max={30}
                          step={1}
                          data-testid="logo-size-slider"
                          className="mt-2"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <Button
            onClick={handleGenerate}
            disabled={loading}
            className="w-full h-12 text-lg font-semibold"
            data-testid="generate-images-button"
            style={{
              background: loading ? '#cbd5e1' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
            }}
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Generiere Bilder...
              </>
            ) : (
              'Bilder generieren'
            )}
          </Button>
        </CardContent>

        {/* Image Generation Loading Animation Overlay */}
        {loading && (
          <div className="absolute inset-0 bg-[rgba(11,14,20,0.97)] backdrop-blur-sm flex items-center justify-center rounded-lg z-50">
            <style dangerouslySetInnerHTML={{__html: `
              @keyframes suckInImage {
                0% {
                  opacity: 0;
                  transform: translate(var(--start-x), var(--start-y)) scale(1);
                }
                20% {
                  opacity: 1;
                }
                100% {
                  opacity: 0;
                  transform: translate(0, 0) scale(0.1);
                }
              }
              .particle-image {
                animation: suckInImage 3s ease-in-out infinite;
                position: absolute;
                font-size: 1.5rem;
              }
            `}} />
            
            <div className="text-center relative w-full px-8">
              {/* Animated Image Particles */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                {[...Array(24)].map((_, i) => {
                  const angle = (i * 360) / 24;
                  const distance = 350;
                  const x = Math.cos(angle * Math.PI / 180) * distance;
                  const y = Math.sin(angle * Math.PI / 180) * distance;
                  const icons = ['🎨', '🖼️', '📷', '✨', '🌟', '💫', '🎯', '🚀', '💡', '⭐', '🔥', '🎭'];
                  
                  return (
                    <div
                      key={i}
                      className="particle-image"
                      style={{
                        '--start-x': `${x}px`,
                        '--start-y': `${y}px`,
                        animationDelay: `${i * 0.125}s`
                      }}
                    >
                      {icons[i % icons.length]}
                    </div>
                  );
                })}
              </div>

              {/* Central Vortex */}
              <div className="relative mx-auto mb-8 w-40 h-40">
                <div className="absolute inset-0 border-4 border-cyan-400/30 rounded-full animate-ping" style={{ animationDuration: '2s' }}></div>
                <div className="absolute inset-4 border-4 border-purple-500/50 rounded-full animate-spin" style={{ animationDuration: '3s' }}></div>
                <div className="absolute inset-8 border-4 border-orange-500/50 rounded-full animate-spin" style={{ animationDuration: '2s', animationDirection: 'reverse' }}></div>
                
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="bg-gradient-to-br from-pink-600 to-purple-600 rounded-full p-5 shadow-2xl">
                    <Sparkles className="w-14 h-14 text-white animate-pulse" />
                  </div>
                </div>
              </div>

              <h3 className="text-3xl font-bold text-[#F5F5F7] mb-3 animate-pulse">
                🎨 KI erstellt Ihre Bilder
              </h3>
              <p className="text-[rgba(245,245,247,0.8)] text-lg mb-6">
                <span className="inline-block animate-bounce" style={{ animationDelay: '0s' }}>🖼️</span>
                <span className="mx-2">Kreative Visuals werden generiert</span>
                <span className="inline-block animate-bounce" style={{ animationDelay: '0.2s' }}>✨</span>
              </p>
              
              <div className="max-w-md mx-auto space-y-3 text-left bg-slate-800/80 rounded-lg p-4 shadow-lg border border-slate-700">
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-[rgba(245,245,247,0.8)] font-medium">Nano Banana KI wird initialisiert</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-[rgba(0,242,255,0.1)]0 rounded-full animate-pulse" style={{ animationDelay: '0.3s' }}></div>
                  <span className="text-[rgba(245,245,247,0.8)] font-medium">Bildkomposition wird berechnet</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-3 h-3 bg-yellow-500 rounded-full animate-pulse" style={{ animationDelay: '0.6s' }}></div>
                  <span className="text-[rgba(245,245,247,0.8)] font-medium">Finale Bilder werden gerendert</span>
                </div>
              </div>

              <div className="mt-8 text-center">
                <p className="text-[rgba(245,245,247,0.5)] text-sm">⏱️ Dauert ca. 15-30 Sekunden</p>
                <div className="flex items-center justify-center gap-2 mt-2">
                  <div className="w-2 h-2 bg-pink-500 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
                  <div className="w-2 h-2 bg-[rgba(0,242,255,0.05)]0 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  <div className="w-2 h-2 bg-indigo-500 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                </div>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Premium Targeting Teaser - For Free Users */}
      {!hasSubscription && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-2xl p-5 cursor-pointer transition-all hover:scale-[1.01]"
          style={{ 
            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(168, 139, 250, 0.05) 100%)',
            border: '2px solid rgba(139, 92, 246, 0.3)'
          }}
          onClick={() => setShowTargetingTrigger(true)}
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #A78BFA 100%)' }}>
              <Target className="w-6 h-6 text-white" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="font-bold text-white">Elite-Targeting freischalten</h4>
                <Lock className="w-4 h-4" style={{ color: '#A78BFA' }} />
              </div>
              <p className="text-sm text-[rgba(245,245,247,0.5)]">
                Erreiche exakt die richtigen Kunden: <span className="text-[#A78BFA] font-semibold">vermögende Investoren, PKV-Wechsler, Immobilienkäufer</span> – ohne Testbudget zu verbrennen.
              </p>
            </div>
            <div className="hidden md:block">
              <Button
                size="sm"
                className="font-semibold"
                style={{ background: 'linear-gradient(135deg, #8B5CF6 0%, #A78BFA 100%)' }}
              >
                Freischalten
              </Button>
            </div>
          </div>
        </motion.div>
      )}
      
      {/* Full Upgrade Comparison Button - For Free Users */}
      {!hasSubscription && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <Button
            onClick={() => setShowUpgradeTable(true)}
            variant="outline"
            className="font-semibold px-6"
            style={{ 
              borderColor: '#C9A227',
              color: '#FFD700',
              backgroundColor: 'rgba(201, 162, 39, 0.1)'
            }}
          >
            <Crown className="w-4 h-4 mr-2" />
            Alle Pakete vergleichen – Dein Weg zum Agency-Killer
          </Button>
        </motion.div>
      )}

      {images.length > 0 && (
        <Card className="shadow-lg border-2" style={{ borderColor: 'rgba(0, 242, 255, 0.3)', background: 'linear-gradient(135deg, rgba(0, 242, 255, 0.02) 0%, rgba(139, 92, 246, 0.02) 100%)' }} data-testid="generated-images">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-xl text-white flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-cyan-400" />
              Generierte Bilder
            </CardTitle>
            <Button
              variant="ghost"
              size="sm"
              onClick={clearSavedImages}
              className="text-[rgba(245,245,247,0.5)] hover:text-red-400"
              data-testid="clear-images-btn"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Neu erstellen
            </Button>
          </CardHeader>
          <CardContent>
            {/* Wert-Simulator - Show after generation for Free Users (450€ Agentur-Vergleich) */}
            {!hasSubscription && (
              <WertSimulatorPopup 
                isVisible={showWertSimulator}
                onUpgrade={() => {
                  setShowWertSimulator(false);
                  setShowPaywall(true);
                }}
                onClose={() => setShowWertSimulator(false)}
              />
            )}
            
            {/* Earnings Simulator - Show after generation */}
            <EarningsSimulator isVisible={showEarningsSimulator && hasSubscription} />
            
            <div className="grid md:grid-cols-2 gap-6 mt-4">
              {images.map((image, index) => (
                <div key={index} className="relative group" data-testid={`image-${index}`}>
                  <img
                    src={`data:image/png;base64,${image}`}
                    alt={`Generated ${index + 1}`}
                    className="w-full h-auto rounded-lg shadow-md"
                  />
                  {/* Download Lock Overlay for Free Users */}
                  {!canDownload && (
                    <div 
                      className="absolute top-2 right-2 px-2 py-1 rounded-lg flex items-center gap-1"
                      style={{ backgroundColor: 'rgba(255, 92, 0, 0.9)' }}
                    >
                      <Lock className="w-3 h-3 text-white" />
                      <span className="text-white text-xs font-bold">Download gesperrt</span>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-lg flex flex-col items-center justify-center gap-3">
                    <Dialog>
                      <DialogTrigger asChild>
                        <Button
                          onClick={() => openAdCreativeDialog(image, index)}
                          size="lg"
                          data-testid={`create-ad-${index}`}
                          className="bg-gradient-to-r from-purple-500 to-cyan-500 hover:from-purple-600 hover:to-cyan-600"
                        >
                          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <rect x="3" y="3" width="18" height="18" rx="2" strokeWidth="2"/>
                            <path d="M3 9h18M9 21V9" strokeWidth="2"/>
                          </svg>
                          Ad-Creative erstellen
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto bg-slate-900 border-slate-700 text-white">
                        <DialogHeader>
                          <DialogTitle className="text-white">Ad-Creative mit Design erstellen</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4 py-4">
                          <div className="space-y-2">
                            <Label htmlFor="ad-template" className="text-[rgba(245,245,247,0.8)]">Design-Template</Label>
                            <Select value={adTemplate} onValueChange={setAdTemplate}>
                              <SelectTrigger className="bg-slate-800 border-slate-700 text-white">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-slate-800 border-slate-700">
                                {adTemplates.map((template) => (
                                  <SelectItem key={template.value} value={template.value}>
                                    <div className="py-1">
                                      <div className="font-semibold">{template.label}</div>
                                      <div className="text-xs text-[rgba(245,245,247,0.6)]">{template.description}</div>
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="ad-headline">Überschrift *</Label>
                            <Input
                              id="ad-headline"
                              placeholder="z.B. Ihre finanzielle Zukunft beginnt hier"
                              value={adHeadline}
                              onChange={(e) => setAdHeadline(e.target.value)}
                              data-testid="ad-headline-input"
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="ad-subtext">Beschreibung (Optional)</Label>
                            <Input
                              id="ad-subtext"
                              placeholder="z.B. Professionelle Beratung für Ihre Altersvorsorge"
                              value={adSubtext}
                              onChange={(e) => setAdSubtext(e.target.value)}
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="ad-cta">Call-to-Action Text</Label>
                            <Input
                              id="ad-cta"
                              value={adCta}
                              onChange={(e) => setAdCta(e.target.value)}
                            />
                          </div>

                          <div className="space-y-2">
                            <Label htmlFor="ad-color">Markenfarbe</Label>
                            <div className="flex gap-2">
                              <Input
                                id="ad-color"
                                type="color"
                                value={adBrandColor}
                                onChange={(e) => setAdBrandColor(e.target.value)}
                                className="w-16 h-10"
                              />
                              <Input
                                value={adBrandColor}
                                onChange={(e) => setAdBrandColor(e.target.value)}
                                placeholder="#667eea"
                                className="flex-1"
                              />
                            </div>
                          </div>

                          <Button
                            onClick={handleCreateAdCreative}
                            disabled={creatingAd}
                            className="w-full"
                            style={{
                              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                            }}
                          >
                            {creatingAd ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Erstelle Ad-Creative...
                              </>
                            ) : (
                              'Ad-Creative erstellen'
                            )}
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                    
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleDownload(image, index)}
                        variant="default"
                        size="sm"
                        data-testid={`download-image-${index}`}
                        className={!canDownload ? 'bg-gray-400 hover:bg-[rgba(255,255,255,0.03)]0' : 'bg-purple-600 hover:bg-purple-700'}
                      >
                        {canDownload ? (
                          <Download className="w-4 h-4 mr-2" />
                        ) : (
                          <Lock className="w-4 h-4 mr-2" />
                        )}
                        {canDownload ? 'Herunterladen' : '🔒 Upgrade'}
                      </Button>
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button
                            onClick={() => openTextDialog(image, index)}
                            variant="secondary"
                            size="sm"
                            data-testid={`add-text-${index}`}
                          >
                            <Type className="w-4 h-4 mr-2" />
                            Text
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="sm:max-w-[500px]">
                          <DialogHeader>
                            <DialogTitle>Text zum Bild hinzufügen</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4 py-4">
                            <div className="space-y-2">
                              <Label htmlFor="text-overlay">Text</Label>
                              <Input
                                id="text-overlay"
                                placeholder="z.B. Jetzt beraten lassen!"
                                value={textOverlay}
                                onChange={(e) => setTextOverlay(e.target.value)}
                                data-testid="text-overlay-input"
                              />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label htmlFor="text-font">Schriftart</Label>
                                <Select value={textFont} onValueChange={setTextFont}>
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="DejaVuSans">DejaVu Sans (Modern)</SelectItem>
                                    <SelectItem value="DejaVuSerif">DejaVu Serif (Elegant)</SelectItem>
                                    <SelectItem value="DejaVuSansMono">DejaVu Mono (Tech)</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>

                              <div className="space-y-2">
                                <Label htmlFor="text-position">Position</Label>
                                <Select value={textPosition} onValueChange={setTextPosition}>
                                  <SelectTrigger>
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="top">Oben (Zentriert)</SelectItem>
                                    <SelectItem value="center">Mitte (Zentriert)</SelectItem>
                                    <SelectItem value="bottom">Unten (Zentriert)</SelectItem>
                                    <SelectItem value="top-left">Oben Links</SelectItem>
                                    <SelectItem value="top-right">Oben Rechts</SelectItem>
                                    <SelectItem value="bottom-left">Unten Links</SelectItem>
                                    <SelectItem value="bottom-right">Unten Rechts</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>

                            <div className="space-y-2">
                              <Label htmlFor="text-color">Textfarbe</Label>
                              <div className="flex gap-2">
                                <Input
                                  id="text-color"
                                  type="color"
                                  value={textColor}
                                  onChange={(e) => setTextColor(e.target.value)}
                                  className="w-16 h-10"
                                />
                                <Input
                                  value={textColor}
                                  onChange={(e) => setTextColor(e.target.value)}
                                  placeholder="#FFFFFF"
                                  className="flex-1"
                                />
                              </div>
                            </div>

                            <div className="space-y-2">
                              <Label htmlFor="text-size">Schriftgröße: {textFontSize[0]}px</Label>
                              <Slider
                                id="text-size"
                                value={textFontSize}
                                onValueChange={setTextFontSize}
                                min={20}
                                max={120}
                                step={5}
                              />
                            </div>

                            <div className="space-y-2">
                              <Label htmlFor="text-bg">Hintergrundfarbe (Optional)</Label>
                              <div className="flex gap-2">
                                <Input
                                  id="text-bg"
                                  type="color"
                                  value={textBackground || '#000000'}
                                  onChange={(e) => setTextBackground(e.target.value)}
                                  className="w-16 h-10"
                                />
                                <Input
                                  value={textBackground}
                                  onChange={(e) => setTextBackground(e.target.value)}
                                  placeholder="Leer = Transparent"
                                  className="flex-1"
                                />
                                {textBackground && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setTextBackground('')}
                                  >
                                    <X className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </div>

                            <Button
                              onClick={handleAddText}
                              disabled={addingText}
                              className="w-full"
                              style={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                              }}
                            >
                              {addingText ? (
                                <>
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  Füge Text hinzu...
                                </>
                              ) : (
                                'Text hinzufügen'
                              )}
                            </Button>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Paywall Popup */}
      <PaywallPopup 
        isOpen={showPaywall} 
        onClose={() => setShowPaywall(false)} 
        type="subscription" 
      />
      
      {/* ===== CONVERSION TRIGGER MODALS ===== */}
      
      {/* Sparten-Trigger Modal - Bei gesperrten Templates */}
      <SpartenTriggerModal 
        isOpen={showSpartenTrigger}
        onClose={() => setShowSpartenTrigger(false)}
        onUpgrade={() => {
          setShowSpartenTrigger(false);
          setShowPaywall(true);
        }}
      />
      
      {/* Launch-Trigger Modal - Bei Meta-Sync Klick */}
      <LaunchTriggerModal 
        isOpen={showLaunchTrigger}
        onClose={() => setShowLaunchTrigger(false)}
        onUpgrade={() => {
          setShowLaunchTrigger(false);
          setShowPaywall(true);
        }}
      />
      
      {/* Targeting-Trigger Modal - Bei gesperrten Zielgruppen */}
      <TargetingTriggerModal 
        isOpen={showTargetingTrigger}
        onClose={() => setShowTargetingTrigger(false)}
        onUpgrade={() => {
          setShowTargetingTrigger(false);
          setShowPaywall(true);
        }}
      />
      
      {/* Upgrade Comparison Table - Full Feature Overview */}
      <UpgradeComparisonTable 
        isOpen={showUpgradeTable}
        onClose={() => setShowUpgradeTable(false)}
        onSelectPlan={(plan) => {
          setShowUpgradeTable(false);
          setShowPaywall(true);
        }}
      />
      
      {/* Onboarding Slideshow - Shows after first creative generation for free users */}
      <OnboardingSlideshow 
        isOpen={showOnboardingSlideshow}
        onClose={() => setShowOnboardingSlideshow(false)}
        onSelectPlan={(plan) => {
          setShowOnboardingSlideshow(false);
          setShowPaywall(true);
        }}
      />
    </div>
    </>
  );
};

export default ImageGenerator;
